<template>
  <div>
    <!-- Mobile overlay -->
    <div
      v-if="isSidebarOpen && !isMediumScreen"
      class="fixed inset-0 z-40 bg-black bg-opacity-50 transition-opacity duration-300 md:hidden"
      @click="handleToggleSidebar"
    ></div>

    <!-- Sidebar -->
    <aside
      :class="[
        'fixed left-0 z-40 h-screen transition-transform bg-blue-900 border-r',
        isSidebarOpen || isMediumScreen ? 'translate-x-0' : '-translate-x-full',
        'md:translate-x-0',
        isCollapsed ? 'w-16' : 'w-64'
      ]"
      aria-label="Sidenav"
      id="drawer-navigation"
    >
      <div class="flex items-center justify-between px-4 py-3">
        <div class="flex items-center justify-center py-1">
          <div v-if="!isCollapsed" class="flex items-center">
            <img src="@/assets/images/Jiji-Health-White-Logo.png" alt="Facility Logo" class="object-cover w-18 h-10" @error="onImageError" />
          </div>
        </div>
        <!-- Toggle button -->
        <button @click="toggleCollapse" 
          class="text-white focus:outline-none">
          <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-width="2" d="M5 7h14M5 12h14M5 17h14" />
          </svg>
        </button>
      </div>
      <div class="overflow-y-auto py-3 px-3 h-full bg-blue-900">
        <ul class="space-y-2">
          <li v-for="(item, index) in sidebarItems" :key="index">
            <div v-if="item.children && item.children.length > 0">
              <button type="button"
                class="flex items-center p-2 w-full text-base font-medium text-gray-900 rounded-md transition duration-75 group dark:text-white hover:bg-[#fdb74e]"
                @click="toggleDropdown(index)">
                <span v-html="item.icon" class="w-6 h-6 text-gray-800 dark:text-white"></span>
                <span v-if="!isCollapsed" class="flex-1 ml-3 text-left whitespace-nowrap">{{ item.label }}</span>
                <svg v-if="!isCollapsed" aria-hidden="true" class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20"
                  xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"></path>
                </svg>
              </button>
              <ul v-if="item.isOpen && !isCollapsed" class="py-2 space-y-2">
                <li v-for="(child, childIndex) in item.children" :key="childIndex">
                  <a
                    href="#"
                    @click.prevent="handleNavigation(child.path)"
                    class="flex items-center p-2 pl-11 w-full text-base font-medium text-gray-900 rounded-md transition duration-75 group dark:text-white hover:bg-[#fdb74e]"
                    :class="{ 'bg-orange-400': $route.path === child.path }"
                  >
                    {{ child.label }}
                  </a>
                </li>
              </ul>
            </div>
            <div v-else>
              <a
                href="#"
                @click.prevent="handleNavigation(item.path)"
                class="flex items-center p-2 text-base font-medium text-gray-900 rounded-md dark:text-white hover:bg-[#fdb74e] group"
                :class="{ 'bg-orange-400': $route.path === item.path }"
              >
                <span v-html="item.icon" class="w-6 h-6 text-gray-800 dark:text-white"></span>
                <span v-if="!isCollapsed" class="ml-3">{{ item.label }}</span>
              </a>
            </div>
          </li>
        </ul>
      </div>
    </aside>
  </div>
</template>

<script>
export default {
  props: {
    isSidebarOpen: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-sidebar', 'update:collapsed', 'navigate'],
  data() {
    return {
      isCollapsed: false,
      isMediumScreen: false,
      sidebarItems: [
        {
          label: "E-Learning",
          path: "/e-learning",
          icon: `<svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.03v13m0-13c-2.819-.831-4.715-1.076-8.029-1.023A.99.99 0 0 0 3 6v11c0 .563.466 1.014 1.03 1.007 3.122-.043 5.018.212 7.97 1.023m0-13c2.819-.831 4.715-1.076 8.029-1.023A.99.99 0 0 1 21 6v11c0 .563-.466 1.014-1.03 1.007-3.122-.043-5.018.212-7.97 1.023"/>
          </svg>`,
          children: [],
          isOpen: false,
        },
        {
          label: "Doctor Bookings",
          path: "/doctor-bookings",
          icon: `<svg class="w-6 h-6 aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-width="2" d="M3 11h18M3 15h18M8 10.792V19m4-8.208V19m4-8.208V19M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z"/>
          </svg>`,
          children: [],
          isOpen: false,
        },
        {
          label: "Rafiki360",
          path: "/rafiki360",
          icon: `<svg class="w-6 h-6 aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-width="2" d="M3 11h18M3 15h18M8 10.792V19m4-8.208V19m4-8.208V19M4 19h16a1 1 0 0 0 1-1V6a1 1 0 0 0-1-1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1Z"/>
          </svg>`,
          children: [],
          isOpen: false,
        },
        {
          label: "Appeals & Donations",
          path: "/appeals-donations",
          icon: `<svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
          </svg>`,
          children: [],
          isOpen: false,
        }
      ],
    };
  },
  watch: {
    isCollapsed(newVal) {
      this.$emit('update:collapsed', newVal);
    }
  },
  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    toggleDropdown(index) {
      this.sidebarItems = this.sidebarItems.map((item, i) => ({
        ...item,
        isOpen: i === index ? !item.isOpen : item.isOpen,
      }));
    },
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
    },
    checkScreenSize() {
      this.isMediumScreen = window.innerWidth >= 768;
    },
    handleToggleSidebar() {
      this.$emit('toggle-sidebar');
    },
    handleNavigation(path) {
      this.$emit('navigate', path); // Emit navigate event with path
    },
    onImageError(event) {
      event.target.src = '/path/to/fallback/image.png'; // Replace with your fallback image path
    },
  },
};
</script>

<style>
.sidebar-enter-active, .sidebar-leave-active {
  transition: transform 0.3s ease;
}
.sidebar-enter-from, .sidebar-leave-to {
  transform: translateX(-100%);
}
</style>